/**
 * Challenge info tab — description, link, tags, details grid,
 * prizes section, sponsors, created date.
 */

import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  ExternalLink, Trophy, Hash, Calendar, Clock,
  Users, Shield, Layers, Percent,
} from 'lucide-react'
import { format } from 'date-fns'

import type { ChallengeDetail, Prize, Sponsor } from '@turnifly/shared/types'

// ── Props ────────────────────────────────────────────────────

interface ChallengeInfoTabProps {
  challenge: ChallengeDetail
  prizes: Prize[]
  sponsors: Sponsor[]
}

// ── Info stat cell ───────────────────────────────────────────

function InfoStatCell({ icon: Icon, color, value, label }: {
  icon: React.FC<{ size: number; style?: React.CSSProperties }>
  color: string
  value: string
  label: string
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={15} style={{ color }} />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[13px] font-bold text-white">{value}</span>
        <span className="truncate text-[10px] text-[var(--color-text-hint)]">{label}</span>
      </div>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────

export function ChallengeInfoTab({
  challenge,
  prizes,
  sponsors,
}: ChallengeInfoTabProps): React.ReactElement {
  const { t } = useTranslation()

  const typeLabels: Record<string, string> = {
    private: '🔒 ' + t('challenges.types.private'),
    public: '🌍 ' + t('challenges.types.public'),
    competition: '🏆 ' + t('challenges.types.competition'),
    platform_free: '📱 Turnifly',
    platform_pro: '💎 Pro',
  }

  const timeModelLabel =
    challenge.time_model === 'fixed'
      ? t('challenges.profile.timeModelFixed')
      : t('challenges.profile.timeModelEvergreen')

  const policyLabels: Record<string, string> = {
    before_start: t('challenges.profile.policyBeforeStart'),
    first_3_days: t('challenges.profile.policyFirst3Days'),
    anytime: t('challenges.profile.policyAnytime'),
  }

  const genderLabels: Record<string, string> = {
    all: t('challenges.gender.all'),
    female: t('challenges.gender.female'),
    male: t('challenges.gender.male'),
  }

  return (
    <div className="space-y-3 px-4 py-3">
      {/* Description card */}
      {challenge.description && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-2xl bg-white/[0.04] p-4"
        >
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-text-hint)]/50">
            {t('challenges.profile.description')}
          </p>
          <p className="whitespace-pre-line text-[13px] leading-relaxed text-white/80">
            {challenge.description}
          </p>
        </motion.div>
      )}

      {/* Link */}
      {challenge.link_url && (
        <a
          href={challenge.link_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2.5 rounded-xl bg-white/[0.04] px-4 py-3.5 transition-colors hover:bg-white/[0.06] tap-highlight-transparent"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)]/15">
            <ExternalLink size={14} className="text-[var(--color-primary)]" />
          </div>
          <span className="flex-1 truncate text-[13px] font-semibold text-[var(--color-primary)]">
            {challenge.link_title || challenge.link_url}
          </span>
        </a>
      )}

      {/* Tags */}
      {challenge.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {challenge.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-[11px] font-semibold text-[var(--color-primary)]"
            >
              <Hash size={10} />
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Details grid */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="rounded-2xl bg-white/[0.04] p-4"
      >
        <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-text-hint)]/50">
          {t('challenges.profile.details')}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <InfoStatCell
            icon={Layers} color="#8B5CF6"
            value={typeLabels[challenge.type] ?? challenge.type}
            label={t('challenges.profile.type')}
          />
          <InfoStatCell
            icon={Clock} color="#F59E0B"
            value={timeModelLabel}
            label={t('challenges.profile.timeModel')}
          />
          <InfoStatCell
            icon={Calendar} color="#3B82F6"
            value={`${challenge.duration_days} ${t('challenges.days')}`}
            label={t('challenges.profile.duration')}
          />
          <InfoStatCell
            icon={Shield} color="#22C55E"
            value={policyLabels[challenge.join_policy] ?? challenge.join_policy}
            label={t('challenges.profile.joinPolicy')}
          />
          <InfoStatCell
            icon={Users} color="#EC4899"
            value={genderLabels[challenge.gender_filter] ?? challenge.gender_filter}
            label={t('challenges.profile.gender')}
          />
          {challenge.max_participants && (
            <InfoStatCell
              icon={Users} color="#14B8A6"
              value={String(challenge.max_participants)}
              label={t('challenges.profile.maxMembers')}
            />
          )}
          {challenge.start_date && (
            <InfoStatCell
              icon={Calendar} color="#22C55E"
              value={format(new Date(challenge.start_date), 'dd.MM.yyyy')}
              label={t('challenges.profile.starts')}
            />
          )}
          {challenge.end_date && (
            <InfoStatCell
              icon={Calendar} color="#EF4444"
              value={format(new Date(challenge.end_date), 'dd.MM.yyyy')}
              label={t('challenges.profile.ends')}
            />
          )}
          {challenge.is_paid && challenge.commission_rate > 0 && (
            <InfoStatCell
              icon={Percent} color="#F97316"
              value={`${Math.round(challenge.commission_rate * 100)}%`}
              label={t('challenges.profile.commissionRate')}
            />
          )}
        </div>
      </motion.div>

      {/* Prizes */}
      {prizes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="rounded-2xl bg-white/[0.04] p-4"
        >
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FFD700]/15">
              <Trophy size={16} className="text-[#FFD700]" />
            </div>
            <p className="text-[13px] font-bold text-white">
              {t('challenges.profile.prizes')}
            </p>
          </div>
          <div className="space-y-2">
            {prizes.map((prize, i) => (
              <div
                key={`${prize.place_from}-${prize.place_to}`}
                className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-3.5 py-2.5"
              >
                <span className="text-base">
                  {i < 3 ? ['🥇', '🥈', '🥉'][i] : '🏅'}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="text-[12px] font-bold tabular-nums text-[var(--color-text-hint)]">
                    {prize.place_from === prize.place_to
                      ? t('challenges.profile.placeFirst', { place: prize.place_from })
                      : t('challenges.profile.place', { from: prize.place_from, to: prize.place_to })}
                  </span>
                  <p className="truncate text-[13px] text-white">
                    {prize.prize_description ?? prize.prize_value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <div className="rounded-2xl bg-white/[0.04] p-4">
          <p className="mb-3 text-[13px] font-bold text-white">
            {t('challenges.profile.sponsors')}
          </p>
          <div className="flex gap-3 overflow-x-auto">
            {sponsors.map((s) => (
              <a
                key={s.id}
                href={s.link_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.04]"
              >
                {s.logo_url ? (
                  <img src={s.logo_url} alt="" className="h-12 w-12 object-contain" loading="lazy" />
                ) : (
                  <span className="text-[10px] text-[var(--color-text-hint)]">Logo</span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Created date */}
      {challenge.created_at && (
        <p className="pb-4 pt-2 text-center text-[11px] text-[var(--color-text-hint)]/30">
          {t('challenges.profile.created')}: {format(new Date(challenge.created_at), 'dd.MM.yyyy')}
        </p>
      )}
    </div>
  )
}
